import { useState, useEffect } from 'react';
import axios from 'axios';

export const useBatchAnalysis = (localMovies, setLocalMovies, showError, showSuccess, setUser) => {
    const [analysisQueue, setAnalysisQueue] = useState([]);
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // Analysis queue processor
    useEffect(() => {
        if (analysisQueue.length > 0 && !isAnalyzing) {
            setIsAnalyzing(true);
            processAnalysisQueue();
        }
    }, [analysisQueue, isAnalyzing]);

    const processAnalysisQueue = async () => {
        if (analysisQueue.length === 0) {
            setIsAnalyzing(false);
            return;
        }

        const { movieId, type } = analysisQueue[0];
        const endpoint = type === 'pcr' ? `/analysis/pcr/${movieId}`
            : type === 'prep' ? `/analysis/prep/${movieId}`         // admin-only minimal tier
            : type === 'deep_focus' ? `/analysis/deep-focus/${movieId}` // Deluxe full analysis
            : `/analysis/director/${movieId}`;                       // Standard full analysis

        // Both full-analysis tiers land the movie in the same 'director_started'
        // pipeline server-side, so mirror that optimistically for the UI.
        const startedStatus = (type === 'director' || type === 'deep_focus')
            ? 'director_started'
            : `${type}_started`;

        try {
            const response = await axios.post(endpoint, { movieId }, {
                timeout: 30000,
            });

            if (response.status === 200 || response.status === 201) {
                setLocalMovies(prev => prev.map(movie =>
                    movie.id === movieId
                        ? { ...movie, status: startedStatus }
                        : movie
                ));

                // Update user credits if returned from server
                if (response.data && setUser) {
                    console.log('Analysis response data:', response.data, 'Analysis type:', type);
                    if (type === 'pcr' && response.data.remaining_credits !== undefined) {
                        console.log('Updating PCR credits to:', response.data.remaining_credits);
                        setUser(prev => ({ ...prev, pcr_credits: response.data.remaining_credits }));
                    } else if (type === 'director' && response.data.remaining_credits !== undefined) {
                        console.log('Updating movie credits to:', response.data.remaining_credits);
                        setUser(prev => ({ ...prev, movie_credits: response.data.remaining_credits }));
                    } else if (type === 'deep_focus' && response.data.remaining_credits !== undefined) {
                        console.log('Updating deluxe movie credits to:', response.data.remaining_credits);
                        setUser(prev => ({ ...prev, movie_credits_deluxe: response.data.remaining_credits }));
                    }
                }

                showSuccess(`Started ${type === 'deep_focus' ? 'deep focus' : type} analysis`);
            } else {
                throw new Error('Unexpected response from server');
            }

            setAnalysisQueue(prev => prev.slice(1));

            setTimeout(() => {
                setIsAnalyzing(false);
            }, 3000);

        } catch (error) {
            console.error('Analysis error:', error);
            
            let errorMessage = 'Analysis failed';
            
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.code === 'ECONNABORTED') {
                errorMessage = 'Request timeout - analysis service unavailable';
            } else if (error.message) {
                errorMessage = error.message;
            }

            setLocalMovies(prev => prev.map(movie =>
                movie.id === movieId
                    ? { 
                        ...movie, 
                        status: `${type}_failed`,
                        errorPreview: errorMessage.length > 50 ? errorMessage.substring(0, 50) + '...' : errorMessage
                      }
                    : movie
            ));
            
            const displayError = errorMessage.length > 50 ? errorMessage.substring(0, 50) + '...' : errorMessage;
            showError(`Failed to start ${type} analysis: ${displayError}`);
            
            setAnalysisQueue(prev => prev.slice(1));
            
            setTimeout(() => {
                setIsAnalyzing(false);
            }, 3000);
        }
    };

    const handleAnalyzeSmallAll = () => {
        try {
            const eligibleMovies = localMovies.filter(movie => movie.status === 'uploaded');
            
            if (eligibleMovies.length === 0) {
                showError('No movies eligible for small analysis');
                return;
            }

            const newAnalysisItems = eligibleMovies.map(movie => ({
                movieId: movie.id,
                type: 'pcr',
            }));

            setAnalysisQueue(prev => [...prev, ...newAnalysisItems]);
            showSuccess(`Added ${eligibleMovies.length} movie(s) to analysis queue`);

        } catch (error) {
            console.error('Error starting batch analysis:', error);
            showError('Error starting batch analysis');
        }
    };

    const handleAnalyzeFull = (movieId) => {
        try {
            const movie = localMovies.find(m => m.id === movieId);
            
            if (!movie || !['uploaded', 'full_pcr'].includes(movie.status)) {
                showError('Movie not eligible for full analysis');
                return;
            }

            setAnalysisQueue(prev => [...prev, { movieId, type: 'director' }]);
            showSuccess('Added movie to analysis queue');

        } catch (error) {
            console.error('Error starting director\'s cut analysis:', error);
            showError('Error starting director\'s cut analysis');
        }
    };

    const handleAnalyzeDeepFocus = (movieId) => {
        try {
            const movie = localMovies.find(m => m.id === movieId);

            if (!movie || !['uploaded', 'full_pcr'].includes(movie.status)) {
                showError('Movie not eligible for deep focus analysis');
                return;
            }

            setAnalysisQueue(prev => [...prev, { movieId, type: 'deep_focus' }]);
            showSuccess('Added movie to analysis queue');

        } catch (error) {
            console.error('Error starting deep focus analysis:', error);
            showError('Error starting deep focus analysis');
        }
    };

    const handleAnalyzePCR = (movieId) => {
        try {
            const movie = localMovies.find(m => m.id === movieId);
            
            if (!movie || !['uploaded'].includes(movie.status)) {
                showError('Movie not eligible for PCR');
                return;
            }

            setAnalysisQueue(prev => [...prev, { movieId, type: 'pcr' }]);
            showSuccess('Added movie to analysis queue');
        } catch (error) {
            console.error('Error starting pcr analysis:', error);
            showError('Error starting pcr analysis');
        }
    };

    // ADMIN ONLY — minimal "prep" tier (split + acts/sequences + summaries, no scoring).
    // Backend re-checks is_admin; these handlers just queue the work.
    const handleAnalyzePrep = (movieId) => {
        try {
            const movie = localMovies.find(m => m.id === movieId);
            if (!movie || !['uploaded', 'full_pcr'].includes(movie.status)) {
                showError('Movie not eligible for prep');
                return;
            }
            setAnalysisQueue(prev => [...prev, { movieId, type: 'prep' }]);
            showSuccess('Added movie to prep queue');
        } catch (error) {
            console.error('Error starting prep analysis:', error);
            showError('Error starting prep analysis');
        }
    };

    const handleAnalyzePrepAll = () => {
        try {
            const eligible = localMovies.filter(m => ['uploaded', 'full_pcr'].includes(m.status));
            if (eligible.length === 0) {
                showError('No movies eligible for prep');
                return;
            }
            setAnalysisQueue(prev => [...prev, ...eligible.map(m => ({ movieId: m.id, type: 'prep' }))]);
            showSuccess(`Added ${eligible.length} movie(s) to prep queue`);
        } catch (error) {
            console.error('Error starting batch prep:', error);
            showError('Error starting batch prep');
        }
    };

    return {
        analysisQueue,
        isAnalyzing,
        handleAnalyzeSmallAll,
        handleAnalyzeFull,
        handleAnalyzeDeepFocus,
        handleAnalyzePCR,
        handleAnalyzePrep,
        handleAnalyzePrepAll
    };
};